import React, { createContext, useContext, useState, ReactNode } from 'react';
import CreditErrorModal, { CreditErrorInfo } from './CreditErrorModal';

interface CreditErrorContextType {
  showCreditError: (errorInfo: CreditErrorInfo) => void;
  hideCreditError: () => void;
  isErrorVisible: boolean;
}

const CreditErrorContext = createContext<CreditErrorContextType | undefined>(undefined);

export const useCreditError = () => {
  const context = useContext(CreditErrorContext);
  if (context === undefined) {
    throw new Error('useCreditError must be used within a CreditErrorProvider');
  }
  return context;
};

interface CreditErrorProviderProps {
  children: ReactNode;
}

export const CreditErrorProvider: React.FC<CreditErrorProviderProps> = ({ children }) => {
  const [errorInfo, setErrorInfo] = useState<CreditErrorInfo | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const showCreditError = (errorInfo: CreditErrorInfo) => {
    setErrorInfo(errorInfo);
    setIsVisible(true);
  };

  const hideCreditError = () => {
    setIsVisible(false);
    setErrorInfo(null);
  };

  const value: CreditErrorContextType = {
    showCreditError,
    hideCreditError,
    isErrorVisible: isVisible,
  };

  return (
    <CreditErrorContext.Provider value={value}>
      {children}
      <CreditErrorModal
        isOpen={isVisible}
        onClose={hideCreditError}
        errorInfo={errorInfo}
      />
    </CreditErrorContext.Provider>
  );
};